if (!customElements.get('remove-gift-wrap')) {
  customElements.define(
    'remove-gift-wrap',
    class RemoveGiftWrap extends HTMLAnchorElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          event.preventDefault();
          const cartItems = this.closest('cart-items');
          if (cartItems && typeof cartItems.enableLoading === 'function') {
            cartItems.enableLoading(this.getAttribute('data-index'));
          }
          const giftWrapping = document.querySelector('gift-wrapping');
          if (giftWrapping) giftWrapping.removeGiftWrap();
        });
      }
    },
    { extends: 'a' }
  );
}

if (!customElements.get('gift-wrapping')) {
  customElements.define(
    'gift-wrapping',
    class GiftWrapping extends HTMLElement {
      constructor() {
        super();
        this.giftWrapId = Number(this.getAttribute('data-gift-wrap-id'));
        this.giftWrapping = this.getAttribute('data-gift-wrapping') || '';
        this.cartItemsSize = parseInt(this.getAttribute('cart-items-size')) || 0;
        this.giftWrapsInCart = parseInt(this.getAttribute('gift-wraps-in-cart')) || 0;

        this.checkbox = this.querySelector('[name="attributes[gift-wrapping]"]');
        this.qtyInput = this.querySelector('[data-gift-qty]');
        this.btnDec = this.querySelector('[data-qty-decr]');
        this.btnInc = this.querySelector('[data-qty-incr]');
        this.loader = this.querySelector('.loader');
      }

      connectedCallback() {
        // Initialize qty input with current cart value if present
        if (this.qtyInput) {
          const initQty = this.giftWrapsInCart > 0 ? this.giftWrapsInCart : 1;
          this.qtyInput.value = initQty;
        }

        // Toggle handler
        if (this.checkbox) {
          this.checkbox.addEventListener(
            'change',
            theme.utils.debounce((event) => {
              event.target.checked ? this.setGiftWrap(this.getQty()) : this.removeGiftWrap();
            }, 300)
          );
        }

        // Qty input change
        if (this.qtyInput) {
          this.qtyInput.addEventListener(
            'change',
            theme.utils.debounce(() => {
              let qty = this.getQty();
              if (!this.checkbox || !this.checkbox.checked) {
                if (this.checkbox) this.checkbox.checked = true;
              }
              this.setGiftWrap(qty);
            }, 300)
          );
        }

        // +/- buttons
        if (this.btnDec) {
          this.btnDec.addEventListener('click', () => {
            let q = this.getQty();
            q = Math.max(1, q - 1);
            this.setQty(q);
            if (!this.checkbox || !this.checkbox.checked) {
              if (this.checkbox) this.checkbox.checked = true;
            }
            this.setGiftWrap(q);
          });
        }
        if (this.btnInc) {
          this.btnInc.addEventListener('click', () => {
            let q = this.getQty();
            q = q + 1;
            this.setQty(q);
            if (!this.checkbox || !this.checkbox.checked) {
              if (this.checkbox) this.checkbox.checked = true;
            }
            this.setGiftWrap(q);
          });
        }

        // Keep UI in sync with any cart updates (e.g., if qty changed via main cart line controls)
        if (theme?.pubsub?.subscribe && theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
          this.unsubscribe = theme.pubsub.subscribe(
            theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            ({ cart }) => this.syncFromCart(cart)
          );
        }

        // Auto-fixes on load: keep attribute and qty aligned.
        if (this.giftWrapsInCart > 0 && this.giftWrapping.length === 0) {
          // Attribute missing, set it without changing qty
          return this.setGiftWrap(this.giftWrapsInCart);
        }
        if (this.giftWrapsInCart === 0 && this.giftWrapping.length > 0) {
          // Attribute present but no line—add with qty from input or 1
          return this.setGiftWrap(this.getQty());
        }
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      // ----- helpers -----
      getQty() {
        const q = parseInt(this.qtyInput ? this.qtyInput.value : '1', 10);
        return isNaN(q) || q < 1 ? 1 : q;
      }

      setQty(q) {
        if (this.qtyInput) this.qtyInput.value = Math.max(1, parseInt(q || 1, 10));
      }

      syncFromCart(cart) {
        const line = cart?.items?.find((it) => it.id === this.giftWrapId);
        const qty = line ? line.quantity : 0;

        // Update UI controls
        if (this.checkbox) this.checkbox.checked = qty > 0;
        if (this.qtyInput) this.qtyInput.disabled = false;
        if (this.btnDec) this.btnDec.disabled = false;
        if (this.btnInc) this.btnInc.disabled = false;
        if (this.checkbox) this.checkbox.disabled = false;
        if (this.loader) this.loader.hidden = true;

        if (qty > 0) {
          this.setQty(qty);
        } else {
          // Clear note field visibility via attribute if your Liquid depends on it
          // (attribute itself is maintained server-side; here we just reflect UI)
          this.setQty(1);
        }
      }

      // ----- actions -----
      setGiftWrap(qty = 1) {
        this.enableLoading();

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({
          updates: { [this.giftWrapId]: qty },
          attributes: { 'gift-wrapping': true },
          sections: sectionsToBundle
        });

        this.fetchGiftWrap(body);
      }

      removeGiftWrap() {
        this.enableLoading();

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({
          updates: { [this.giftWrapId]: 0 },
          attributes: { 'gift-wrapping': '', 'gift-note': '' },
          sections: sectionsToBundle
        });

        this.fetchGiftWrap(body);
      }

      fetchGiftWrap(body) {
        fetch(`${theme.routes.cart_update_url}`, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((r) => r.json())
          .then((parsedState) => {
            // Publish, which triggers syncFromCart to re-enable controls
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'gift-wrapping',
              cart: parsedState
            });
          })
          .catch((error) => console.log(error))
          .finally(() => this.disableLoading());
      }

      enableLoading() {
        if (this.loader) this.loader.hidden = false;
        if (this.checkbox) this.checkbox.disabled = true;
        if (this.qtyInput) this.qtyInput.disabled = true;
        if (this.btnDec) this.btnDec.disabled = true;
        if (this.btnInc) this.btnInc.disabled = true;
      }

      disableLoading() {
        if (this.loader) this.loader.hidden = true;
        if (this.checkbox) this.checkbox.disabled = false;
        if (this.qtyInput) this.qtyInput.disabled = false;
        if (this.btnDec) this.btnDec.disabled = false;
        if (this.btnInc) this.btnInc.disabled = false;
      }
    }
  );
}

if (!customElements.get('gift-note')) {
  customElements.define(
    'gift-note',
    class GiftNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'change',
          theme.utils.debounce((event) => {
            const body = JSON.stringify({ attributes: { 'gift-note': event.target.value } });
            fetch(`${theme.routes.cart_update_url}`, { ...theme.utils.fetchConfig(), ...{ body } });
          }, 300)
        );
      }
    }
  );
}
